import React, { useEffect, useMemo, useState } from 'react';
import { Select, Tag, Space, Spin, Tooltip } from 'antd';
import moment from 'moment-timezone';
import UserAvatar from './UserAvatar';
import CalendarDrawer from '../user-management/CalendarDrawer';
import { getCompanyUsers, getCalendarPreview } from '../user-management/api';
import session from '../../utils/session';

// Scheduling-manager: pick which teammate's calendar an interview invite books
// onto. Reuses the company-users list (which flags has_connected_calendar), the
// by-user availability preview, and the CalendarDrawer to view a teammate's
// calendar. Supports single-select (1-1 invites, one candidate → one calendar)
// and multi-select (broadcast round-robin across a team).
//
// Props:
//   value          selected user id (single) or array of ids (multiple)
//   onChange       (idOrIds) => void
//   multiple       enable round-robin multi-select (default false)
//   includeSelf    allow choosing the current user (default true)
//   disabled       disable the control
//   size           antd Select size (default 'middle')
//   onSlotCounts   (map userId->count) => void  — lets parents surface aggregates
const availabilityColor = (count) => {
    if (count === null || count === undefined) return 'default';
    if (count === 0) return 'red';
    if (count <= 5) return 'orange';
    return 'green';
};

const availabilityLabel = (count) => {
    if (count === null || count === undefined) return 'Checking…';
    if (count === 0) return 'No slots — next 15 days';
    return `${count} slot${count === 1 ? '' : 's'} available`;
};

const CalendarOwnerPicker = ({
    value,
    onChange,
    multiple = false,
    includeSelf = true,
    disabled = false,
    size = 'middle',
    compact = false,
    onSlotCounts,
}) => {
    const userData = session.get('userData') || {};
    const companyId = userData.companyId || userData.company_id;
    const currentUserId = String(userData.id || '');

    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [slotCounts, setSlotCounts] = useState({}); // userId -> count | null (loading)
    const [drawerUser, setDrawerUser] = useState(null);

    // Load the company teammates once.
    useEffect(() => {
        if (!companyId) return undefined;
        let cancelled = false;
        setLoadingUsers(true);
        getCompanyUsers(companyId, { pageSize: 100 })
            .then((res) => {
                const rows = res?.data?.rows || res?.data?.data?.rows || [];
                if (!cancelled) setUsers(rows);
            })
            .catch(() => { if (!cancelled) setUsers([]); })
            .finally(() => { if (!cancelled) setLoadingUsers(false); });
        return () => { cancelled = true; };
    }, [companyId]);

    const selectedIds = useMemo(
        () => (multiple ? (value || []) : (value ? [value] : [])),
        [value, multiple],
    );

    // Fetch availability for any selected teammate we haven't counted yet.
    useEffect(() => {
        const missing = selectedIds.filter((id) => !(id in slotCounts));
        if (!missing.length) return;

        // mark as loading
        setSlotCounts((prev) => {
            const next = { ...prev };
            missing.forEach((id) => { next[id] = null; });
            return next;
        });

        const from = moment().format('YYYY-MM-DDTHH:mm:ssZ');
        missing.forEach(async (id) => {
            try {
                const res = await getCalendarPreview(id, from);
                const body = res?.data?.data ?? res?.data ?? [];
                const slots = Array.isArray(body) ? body : (body.schedules || []);
                const count = body && body.error ? 0
                    : slots.filter((s) => s.ghslot && s.available).length;
                setSlotCounts((prev) => ({ ...prev, [id]: count }));
            } catch {
                setSlotCounts((prev) => ({ ...prev, [id]: 0 }));
            }
        });
    }, [selectedIds, slotCounts]);

    // Bubble slot counts up so parents can show aggregates (round-robin).
    useEffect(() => {
        if (onSlotCounts) onSlotCounts(slotCounts);
    }, [slotCounts, onSlotCounts]);

    const options = useMemo(() => {
        // Defensive de-dup by id: the company-users source can occasionally return a
        // user more than once, which would list (and let round-robin pick) the same
        // person twice.
        const seen = new Set();
        const uniqueUsers = users.filter((u) => {
            const key = String(u.id);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        const base = uniqueUsers.map((u) => {
            const isSelf = String(u.id) === currentUserId;
            const connected = !!u.has_connected_calendar;
            // A teammate can only host interviews if they have interview-scheduling
            // access; self is the acting user (already on a scheduling surface) so is
            // always eligible. Fail open when the flag is absent (older backend) —
            // the booking API enforces this authoritatively either way.
            const canSchedule = isSelf || u.has_interview_scheduling !== false;
            return {
                value: u.id,
                disabled: !connected || !canSchedule || (!includeSelf && isSelf),
                label: (
                    <Space size={8}>
                        <UserAvatar name={u.name} userId={u.id} size={22} />
                        <span>{u.name}{isSelf ? ' (You)' : ''}</span>
                        {!connected && <Tag color="default" style={{ marginInlineEnd: 0 }}>No calendar connected</Tag>}
                        {connected && !canSchedule && <Tag color="default" style={{ marginInlineEnd: 0 }}>No scheduling access</Tag>}
                    </Space>
                ),
                // plain text used for search + selected-tag rendering
                title: u.name,
                user: u,
            };
        });

        // Until the teammates list loads, a preselected value (e.g. self, or a
        // restored draft) has no matching option, so antd renders the raw user id.
        // Add a transient "Loading…" option for any selected id we don't have yet so
        // the control never flashes a UUID.
        const known = new Set(base.map((o) => String(o.value)));
        const placeholders = selectedIds
            .filter((id) => !known.has(String(id)))
            .map((id) => ({
                value: id,
                title: 'Loading…',
                label: (
                    <Space size={8}>
                        <Spin size="small" />
                        <span style={{ color: '#8c8c8c' }}>Loading…</span>
                    </Space>
                ),
            }));

        return [...base, ...placeholders];
    }, [users, currentUserId, includeSelf, selectedIds]);

    const selectedUsers = useMemo(
        () => selectedIds.map((id) => users.find((u) => String(u.id) === String(id))).filter(Boolean),
        [selectedIds, users],
    );

    // Compact chips for multi-select (round-robin) — the option label carries a
    // 22px avatar that renders too tall as a selected tag, so draw a small chip.
    const renderTag = ({ value: tagValue, closable, onClose }) => {
        const u = users.find((x) => String(x.id) === String(tagValue));
        const isSelf = String(tagValue) === currentUserId;
        return (
            <Tag
                closable={closable}
                onClose={onClose}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    marginInlineEnd: 4, paddingInlineStart: 3, paddingInlineEnd: 4,
                }}
            >
                {/* Show a spinner rather than the raw id while the teammates list loads. */}
                {u
                    ? <UserAvatar name={u.name} userId={u.id} size={16} showTooltip={false} />
                    : <Spin size="small" />}
                <span>{u ? `${u.name}${isSelf ? ' (You)' : ''}` : 'Loading…'}</span>
            </Tag>
        );
    };

    // Collapsed single-select label — render avatar + name for a resolved user, or a
    // spinner + "Loading…" while the teammates list is still fetching, so the closed
    // selector never falls back to the raw user id (UUID) and keeps the avatar.
    // (Multiple mode uses renderTag instead.)
    const renderSelectedLabel = ({ value: selectedValue }) => {
        const u = users.find((x) => String(x.id) === String(selectedValue));
        if (!u) {
            return (
                <Space size={8}>
                    <Spin size="small" />
                    <span style={{ color: '#8c8c8c' }}>Loading…</span>
                </Space>
            );
        }
        const isSelf = String(u.id) === currentUserId;
        return (
            <Space size={8}>
                <UserAvatar name={u.name} userId={u.id} size={20} showTooltip={false} />
                <span>{u.name}{isSelf ? ' (You)' : ''}</span>
            </Space>
        );
    };

    const selectEl = (
        <Select
            style={{ width: '100%', minWidth: 0 }}
            size={size}
            mode={multiple ? 'multiple' : undefined}
            showSearch
            optionFilterProp="title"
            labelRender={multiple ? undefined : renderSelectedLabel}
            loading={loadingUsers}
            disabled={disabled}
            value={value}
            onChange={onChange}
            placeholder={multiple ? 'Select teammates for round-robin' : "Select a teammate's calendar"}
            options={options}
            tagRender={multiple ? renderTag : undefined}
            maxTagCount="responsive"
        />
    );

    // Compact single-line layout (1-1 inbox): select grows, availability as a
    // small colored pill (tooltip has the full text), calendar as an icon button.
    if (compact && !multiple) {
        const owner = selectedUsers[0];
        const count = owner ? slotCounts[owner.id] : undefined;
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>{selectEl}</div>
                {owner && (
                    <Tooltip title={`${availabilityLabel(count)} — click to view calendar`}>
                        <Tag
                            color={availabilityColor(count)}
                            onClick={() => count !== null && setDrawerUser(owner)}
                            style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, cursor: count === null ? 'default' : 'pointer' }}
                        >
                            {count === null ? <Spin size="small" /> : count}
                        </Tag>
                    </Tooltip>
                )}
                <CalendarDrawer user={drawerUser} onClose={() => setDrawerUser(null)} />
            </div>
        );
    }

    return (
        <div>
            {selectEl}

            {/* Availability badges for the current selection */}
            {selectedUsers.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {selectedUsers.map((u) => {
                        const count = slotCounts[u.id];
                        return (
                            <Tooltip key={u.id} title="Click to view calendar">
                                <Tag
                                    color={availabilityColor(count)}
                                    onClick={() => count !== null && setDrawerUser(u)}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: count === null ? 'default' : 'pointer' }}
                                >
                                    {count === null && <Spin size="small" />}
                                    {multiple ? `${u.name}: ` : ''}{availabilityLabel(count)}
                                </Tag>
                            </Tooltip>
                        );
                    })}
                </div>
            )}

            <CalendarDrawer user={drawerUser} onClose={() => setDrawerUser(null)} />
        </div>
    );
};

export default CalendarOwnerPicker;
